import React, { useState, useRef } from "react";
import axios from "axios";
import { Link, useParams, useNavigate } from "react-router-dom";
import {
  ChevronLeft, ChevronRight, X, Check, ArrowRight, MapPin,
  Clock, Sun, Mountain, Users, Tag, Images, Loader2, Quote,
} from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { useFormatPrice } from "@/contexts/CurrencyContext";
import { EXPERIENCES, HOTELS } from "@/lib/data";
import { getExperienceDetails } from "@/lib/experienceDetails";
import { getExperienceTravelNotes } from "@/lib/experienceTravelNotes";
import TravelNotesCarousel from "@/components/TravelNotesCarousel";
import BrandMonogramEdge from "@/components/BrandMonogramEdge";
import XalucaOriginalsBadge from "@/components/XalucaOriginalsBadge";
import { useHotelPropertyGalleries, buildAboutTiles } from "@/lib/useHotelPropertyGalleries";
import { getExperienceTestimonials } from "@/lib/experienceTestimonials";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const pick = (obj, lang) => {
  if (!obj) return "";
  if (typeof obj === "string") return obj;
  return obj[lang] || obj.en || obj.fr || "";
};

const FORM_LABELS = {
  full_name: { en: "Full name", fr: "Nom complet", es: "Nombre completo" },
  email: { en: "Email", fr: "Email", es: "Correo electrónico" },
  phone: { en: "Phone (optional)", fr: "Téléphone (optionnel)", es: "Teléfono (opcional)" },
  preferred_date: { en: "Preferred date", fr: "Date souhaitée", es: "Fecha preferida" },
  guests: { en: "Guests", fr: "Voyageurs", es: "Huéspedes" },
  notes: { en: "Anything we should know?", fr: "Quelque chose à savoir ?", es: "¿Algo que debamos saber?" },
  submit: { en: "Send request", fr: "Envoyer la demande", es: "Enviar solicitud" },
  sending: { en: "Sending…", fr: "Envoi…", es: "Enviando…" },
  thanks: {
    en: "Request received — our concierge will reply within 24h.",
    fr: "Demande reçue — notre conciergerie vous répond sous 24h.",
    es: "Solicitud recibida — nuestro conserje le responderá en 24h.",
  },
  error: {
    en: "Something went wrong. Please try again or write to us at xaluca@xaluca.com.",
    fr: "Une erreur est survenue. Réessayez ou écrivez-nous à xaluca@xaluca.com.",
    es: "Algo salió mal. Inténtelo de nuevo o escríbanos a xaluca@xaluca.com.",
  },
  title: {
    en: "Request information about this experience",
    fr: "Demander des informations sur cette expérience",
    es: "Solicitar información sobre esta experiencia",
  },
  sub: {
    en: "Tell us when you'd like to go — our concierge will design the day around you.",
    fr: "Dites-nous quand vous souhaitez partir — notre conciergerie composera la journée autour de vous.",
    es: "Díganos cuándo desea ir — nuestro conserje compondrá el día a su medida.",
  },
};

const Lightbox = ({ images, title, onClose }) => {
  const [idx, setIdx] = useState(0);
  React.useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") setIdx((i) => (i + 1) % images.length);
      if (e.key === "ArrowLeft") setIdx((i) => (i - 1 + images.length) % images.length);
    };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [images.length, onClose]);

  return (
    <div className="fixed inset-0 z-[100] bg-[#1A1513]/95 flex items-center justify-center px-4 md:px-12" data-testid="experience-lightbox" role="dialog" aria-modal="true">
      <button onClick={onClose} className="absolute top-6 right-6 text-[#FDFBF7]/80 hover:text-[#FDFBF7] p-2" data-testid="experience-lightbox-close" aria-label="Close">
        <X className="w-6 h-6" strokeWidth={1.5} />
      </button>
      <button onClick={() => setIdx((i) => (i - 1 + images.length) % images.length)} className="absolute left-2 md:left-8 top-1/2 -translate-y-1/2 text-[#FDFBF7]/70 hover:text-[#FDFBF7] p-3" aria-label="Previous">
        <ChevronLeft className="w-8 h-8" strokeWidth={1.5} />
      </button>
      <button onClick={() => setIdx((i) => (i + 1) % images.length)} className="absolute right-2 md:right-8 top-1/2 -translate-y-1/2 text-[#FDFBF7]/70 hover:text-[#FDFBF7] p-3" aria-label="Next">
        <ChevronRight className="w-8 h-8" strokeWidth={1.5} />
      </button>
      <div className="max-w-6xl w-full">
        <img src={images[idx]} alt={`${title} ${idx + 1}`} className="w-full max-h-[78vh] object-contain" loading="lazy" decoding="async"/>
        <div className="mt-6 flex items-center justify-between text-[#FDFBF7]/85">
          <p className="font-serif-x text-xl md:text-2xl">{title}</p>
          <p className="text-xs tracking-[0.25em] uppercase tabular-nums">
            {String(idx + 1).padStart(2, "0")} / {String(images.length).padStart(2, "0")}
          </p>
        </div>
        <div className="mt-4 flex gap-2 overflow-x-auto pb-1">
          {images.map((src, i) => (
            <button
              key={`thumb-${i}`}
              onClick={() => setIdx(i)}
              className={`shrink-0 w-20 h-14 overflow-hidden border-2 transition-opacity ${i === idx ? "border-[#D4A373] opacity-100" : "border-transparent opacity-60 hover:opacity-100"}`}
              aria-label={`Image ${i + 1}`}
            >
              <img src={src} alt="" className="w-full h-full object-cover" loading="lazy" decoding="async"/>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

const ExperienceDetailPage = () => {
  const { key } = useParams();
  const { t, lang } = useLanguage();
  const { parse: parsePrice } = useFormatPrice();
  const navigate = useNavigate();
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const formRef = useRef(null);
  const [form, setForm] = useState({
    full_name: "",
    email: "",
    phone: "",
    preferred_date: "",
    guests: 2,
    notes: "",
  });
  const [status, setStatus] = useState({ submitting: false, success: false, error: false });

  const scrollToForm = () => {
    if (formRef.current) {
      formRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
      const firstInput = formRef.current.querySelector("input[name='full_name']");
      if (firstInput) setTimeout(() => firstInput.focus(), 700);
    }
  };

  const ex = EXPERIENCES.find((e) => e.key === key);
  const d = ex ? getExperienceDetails(key) : null;

  // CMS-editable image collection for this experience — must be called
  // unconditionally so React hook order stays stable. Uses the same
  // property-galleries API as the hotel "About the Hotel" section, with
  // an `experience-` namespaced slug so it lives alongside (and never
  // collides with) hotel galleries.
  const propertyGalleries = useHotelPropertyGalleries(key ? `experience-${key}` : null);

  const submitForm = async (e) => {
    e.preventDefault();
    if (status.submitting) return;
    setStatus({ submitting: true, success: false, error: false });
    try {
      await axios.post(`${API}/experience-requests`, {
        full_name: form.full_name,
        email: form.email,
        phone: form.phone || null,
        experience_key: key,
        experience_title: ex ? ex.title.en : key,
        preferred_date: form.preferred_date || null,
        guests: Number(form.guests) || 2,
        notes: form.notes || null,
        language: lang,
      });
      setStatus({ submitting: false, success: true, error: false });
      setForm({ full_name: "", email: "", phone: "", preferred_date: "", guests: 2, notes: "" });
    } catch {
      setStatus({ submitting: false, success: false, error: true });
    }
  };

  if (!ex || !d) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-32 text-center" data-testid="experience-not-found">
        <p className="font-serif-x text-3xl text-[#2C2621] mb-6">
          {pick({ en: "Experience not found.", fr: "Expérience introuvable.", es: "Experiencia no encontrada." }, lang)}
        </p>
        <Link to="/experiences" className="text-[#C16542] underline">
          ← {t("common.back")}
        </Link>
      </div>
    );
  }

  const relatedHotels = (d.related_hotels || [])
    .map((slug) => HOTELS.find((h) => h.slug === slug))
    .filter(Boolean);

  // 9-tile "About the Hotel"-style image grid for this experience —
  // 1 panoramic featured tile + 8 supporting squares. Falls back to the
  // hard-coded gallery until an admin uploads via the CMS.
  const galleryFallback = [d.hero_image, ...(d.gallery || [])].filter(Boolean);
  const aboutTiles = buildAboutTiles(propertyGalleries, "about", galleryFallback, 9);
  // Tiles drive the lightbox stack so admins control both the grid and
  // the lightbox order from one place.
  const lightboxImages = aboutTiles && aboutTiles.length ? aboutTiles : d.gallery;

  return (
    <div data-testid={`page-experience-${key}`} className="bg-[#FDFBF7] text-[#2C2621]">
      {/* HERO */}
      <section className="relative min-h-[78vh] flex flex-col justify-end" data-testid="experience-hero" data-cms-scope={`experience-detail.${key}`}>
        <img src={d.hero_image} alt={pick(ex.title, lang)} className="absolute inset-0 w-full h-full object-cover" loading="lazy" decoding="async" data-cms-role="hero"/>
        <div className="absolute inset-0 bg-gradient-to-t from-[#1A1513]/92 via-[#1A1513]/45 to-[#1A1513]/15" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 flex flex-col pb-16 text-[#FDFBF7]">
          <button
            onClick={() => navigate(-1)}
            className="mb-6 inline-flex items-center gap-1 text-xs tracking-[0.25em] uppercase text-[#FDFBF7]/80 hover:text-[#D4A373] w-fit"
            data-testid="experience-back-button"
          >
            <ChevronLeft className="w-4 h-4" strokeWidth={1.5} />
            {t("common.back")}
          </button>
          <p className="overline !text-[#D4A373]">
            {pick({ en: "Experience", fr: "Expérience", es: "Experiencia" }, lang)}
          </p>
          <div className="mt-4">
            <XalucaOriginalsBadge variant="dark" testid="experience-hero-originals-badge" />
          </div>
          <h1 className="font-serif-x mt-5 text-5xl md:text-7xl lg:text-8xl tracking-tighter leading-[1.02] max-w-5xl">
            {pick(ex.title, lang)}
          </h1>
          <p className="mt-6 text-xl md:text-2xl font-serif-x italic text-[#D4A373] max-w-3xl">
            {pick(d.tagline, lang)}
          </p>
          <div className="mt-10 flex flex-wrap items-center gap-4">
            <button
              type="button"
              onClick={scrollToForm}
              className="bg-[#C16542] hover:bg-[#A35133] text-[#FDFBF7] px-8 py-4 text-xs tracking-[0.2em] uppercase inline-flex items-center gap-2"
              data-testid="experience-cta-request"
            >
              {pick({ en: "Request information", fr: "Demander des informations", es: "Solicitar información" }, lang)}
              <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
            </button>
            <button
              type="button"
              onClick={() => setLightboxOpen(true)}
              className="border border-[#FDFBF7]/40 text-[#FDFBF7] hover:border-[#FDFBF7] px-6 py-4 text-xs tracking-[0.2em] uppercase inline-flex items-center gap-2"
              data-testid="experience-cta-gallery"
            >
              <Images className="w-3.5 h-3.5" strokeWidth={1.5} />
              {pick({ en: "View gallery", fr: "Voir la galerie", es: "Ver galería" }, lang)}
            </button>
          </div>
        </div>
      <BrandMonogramEdge size="xl" opacity={0.6} testIdPrefix="page-hero-brand" />
      </section>

      {/* INTRO + STORY */}
      <section className="py-20 md:py-28" data-testid="experience-story">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid md:grid-cols-12 gap-12">
          <div className="md:col-span-7">
            <p className="overline">{t("sections.experiences_overline")}</p>
            <p className="font-serif-x mt-4 text-3xl md:text-4xl leading-[1.25] text-[#2C2621]">
              {pick(d.intro, lang)}
            </p>
            <p className="mt-8 text-lg text-[#5C5248] leading-relaxed">
              {pick(d.story, lang)}
            </p>
            {/* Travel notes — handwritten pocket cards, sliding carousel */}
            <div className="mt-12" data-testid={`experience-${key}-travel-notes-wrap`}>
              <TravelNotesCarousel
                notes={getExperienceTravelNotes(key)}
                lang={lang}
                testIdPrefix={`experience-${key}-travel-notes`}
              />
            </div>
          </div>
          <div className="md:col-span-5">
            {/* 9-tile gallery — natural expansion of the "About the
                Hotel" pattern: 1 panoramic featured + 8 squares.
                Sourced from the admin-managed property-galleries API
                (slug "experience-{key}") with hard-coded fallback.
                Managed via /admin/galleries → NOT via image_slots, so
                opt every tile out of the AutoImageEditor to avoid
                duplicate slot entries in /admin/images. */}
            <div className="grid grid-cols-2 gap-3" data-testid="experience-about-gallery" data-no-cms-edit="true">
              {aboutTiles && aboutTiles.map((g, i) => (
                <button
                  key={`exp-gallery-tile-${key}-${i}`}
                  type="button"
                  onClick={() => setLightboxOpen(true)}
                  className={`relative overflow-hidden ${i === 0 ? "col-span-2 aspect-[16/10]" : "aspect-square"}`}
                  data-testid={`experience-about-gallery-${i}`}
                  aria-label={`Open gallery image ${i + 1}`}
                >
                  <img
                    src={g}
                    alt={`${pick(ex.title, lang)} ${i + 1}`}
                    className="absolute inset-0 w-full h-full object-cover" loading="lazy" decoding="async"
                    data-cms-role={`gallery.${i}`}/>
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* HIGHLIGHTS + PRACTICAL */}
      <section className="bg-[#F2EBE1]/50 py-20 md:py-28 border-y border-[#2C2621]/10" data-testid="experience-info">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid md:grid-cols-12 gap-12">
          <div className="md:col-span-7">
            <p className="overline">
              {pick({ en: "Highlights", fr: "Temps forts", es: "Aspectos destacados" }, lang)}
            </p>
            <h2 className="font-serif-x mt-4 text-4xl md:text-5xl tracking-tight leading-[1.05]">
              {pick({ en: "Why guests love it", fr: "Pourquoi les voyageurs l'aiment", es: "Por qué les encanta a nuestros huéspedes" }, lang)}
            </h2>
            <ul className="mt-10 space-y-4">
              {d.highlights.map((h, i) => (
                <li key={`h-${i}`} className="flex items-start gap-4">
                  <span className="mt-1.5 w-6 h-6 rounded-full bg-[#C16542] text-[#FDFBF7] flex items-center justify-center shrink-0">
                    <Check className="w-3.5 h-3.5" strokeWidth={2.2} />
                  </span>
                  <span className="text-lg text-[#2C2621] leading-snug">{pick(h, lang)}</span>
                </li>
              ))}
            </ul>

            {/* Experience-specific testimonials — short quote cards
                rendered inside the same text column, directly below the
                Highlights list. Each card carries a guest name, quote,
                origin city and trip date. */}
            {(() => {
              const testimonials = getExperienceTestimonials(key);
              if (!testimonials.length) return null;
              return (
                <div className="mt-14 pt-10 border-t border-[#2C2621]/12" data-testid="experience-testimonials">
                  <p className="overline">
                    {pick({ en: "Travel notes", fr: "Carnets de voyage", es: "Notas de viaje" }, lang)}
                  </p>
                  <h3 className="font-serif-x mt-3 text-2xl md:text-3xl tracking-tight leading-snug text-[#2C2621]">
                    {pick(
                      {
                        en: "What guests said after this experience",
                        fr: "Ce qu'ils ont dit après cette expérience",
                        es: "Lo que dijeron después de esta experiencia",
                      },
                      lang
                    )}
                  </h3>

                  <ul className="mt-8 space-y-6">
                    {testimonials.map((t, i) => (
                      <li
                        key={`exp-testimonial-${key}-${i}`}
                        className="relative bg-[#FDFBF7] border border-[#E8DFCF] p-6 md:p-7"
                        data-testid={`experience-testimonial-${i}`}
                      >
                        <Quote
                          className="absolute -top-3 left-6 w-7 h-7 text-[#C16542] bg-[#F2EBE1] px-1 rotate-180"
                          strokeWidth={1.5}
                          aria-hidden="true"
                        />
                        <blockquote className="font-serif-x italic text-lg md:text-xl leading-relaxed text-[#2C2621]">
                          “{pick(t.quote, lang)}”
                        </blockquote>
                        <figcaption className="mt-5 pt-4 border-t border-[#2C2621]/8 flex flex-wrap items-baseline gap-x-3 gap-y-1 text-[11px] tracking-[0.22em] uppercase text-[#5C5248]">
                          <span className="font-medium text-[#2C2621]">— {t.name}</span>
                          {t.place && <span className="text-[#5C5248]/85">· {t.place}</span>}
                          {t.when && <span className="text-[#5C5248]/70">· {t.when}</span>}
                        </figcaption>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })()}
          </div>
          <div className="md:col-span-5">
            <div className="bg-[#FDFBF7] p-8 md:p-10 border border-[#E8DFCF]" data-testid="experience-practical">
              <p className="overline">
                {pick({ en: "Practical", fr: "Pratique", es: "Práctico" }, lang)}
              </p>
              <h3 className="font-serif-x mt-3 text-2xl md:text-3xl">
                {pick({ en: "Plan your day", fr: "Préparez votre journée", es: "Planifique su día" }, lang)}
              </h3>
              <ul className="mt-6 space-y-4 text-sm text-[#2C2621]">
                <li className="flex items-start gap-3">
                  <Clock className="w-4 h-4 text-[#C16542] mt-0.5" strokeWidth={1.5} />
                  <div>
                    <div className="overline !text-[#5C5248]">{pick({ en: "Duration", fr: "Durée", es: "Duración" }, lang)}</div>
                    <div className="mt-1">{pick(d.practical.duration, lang)}</div>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <Sun className="w-4 h-4 text-[#C16542] mt-0.5" strokeWidth={1.5} />
                  <div>
                    <div className="overline !text-[#5C5248]">{pick({ en: "Season", fr: "Saison", es: "Temporada" }, lang)}</div>
                    <div className="mt-1">{pick(d.practical.season, lang)}</div>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <Mountain className="w-4 h-4 text-[#C16542] mt-0.5" strokeWidth={1.5} />
                  <div>
                    <div className="overline !text-[#5C5248]">{pick({ en: "Difficulty", fr: "Difficulté", es: "Dificultad" }, lang)}</div>
                    <div className="mt-1">{pick(d.practical.difficulty, lang)}</div>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <Users className="w-4 h-4 text-[#C16542] mt-0.5" strokeWidth={1.5} />
                  <div>
                    <div className="overline !text-[#5C5248]">{pick({ en: "Group", fr: "Groupe", es: "Grupo" }, lang)}</div>
                    <div className="mt-1">{pick(d.practical.group, lang)}</div>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <Tag className="w-4 h-4 text-[#C16542] mt-0.5" strokeWidth={1.5} />
                  <div>
                    <div className="overline !text-[#5C5248]">{pick({ en: "From", fr: "À partir de", es: "Desde" }, lang)}</div>
                    <div className="mt-1 font-medium text-[#C16542]">{parsePrice(pick(d.practical.price, lang)) || pick(d.practical.price, lang)}</div>
                  </div>
                </li>
              </ul>
              <div className="mt-8 pt-6 border-t border-[#E8DFCF]">
                <div className="overline !text-[#5C5248]">{pick({ en: "Included", fr: "Inclus", es: "Incluido" }, lang)}</div>
                <ul className="mt-3 space-y-1.5 text-sm text-[#5C5248]">
                  {d.practical.includes.map((it, i) => (
                    <li key={`inc-${i}`} className="flex items-start gap-2">
                      <Check className="w-3.5 h-3.5 text-[#C16542] mt-1 shrink-0" strokeWidth={2} />
                      <span>{pick(it, lang)}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <button
                type="button"
                onClick={scrollToForm}
                className="mt-8 w-full bg-[#C16542] hover:bg-[#A35133] text-[#FDFBF7] px-6 py-3.5 text-xs tracking-[0.25em] uppercase inline-flex items-center justify-center gap-2"
                data-testid="experience-practical-cta"
              >
                {pick({ en: "Request information", fr: "Demander des informations", es: "Solicitar información" }, lang)}
                <ArrowRight className="w-3.5 h-3.5" strokeWidth={1.5} />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* INLINE REQUEST FORM */}
      <section
        ref={formRef}
        className="py-20 md:py-28 bg-[#1A1513] text-[#FDFBF7]"
        data-testid="experience-request-form-section"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid md:grid-cols-12 gap-12">
          <div className="md:col-span-5">
            <p className="overline !text-[#D4A373]">
              {pick({ en: "Request information", fr: "Demander des informations", es: "Solicitar información" }, lang)}
            </p>
            <h2 className="font-serif-x mt-4 text-4xl md:text-5xl leading-[1.05]">
              {pick(FORM_LABELS.title, lang)}
            </h2>
            <p className="mt-6 text-lg text-[#FDFBF7]/75 leading-relaxed">
              {pick(FORM_LABELS.sub, lang)}
            </p>
            <div className="mt-10 pt-6 border-t border-[#FDFBF7]/15 space-y-3 text-sm text-[#FDFBF7]/70">
              <p className="font-serif-x text-[#D4A373] text-lg">{pick(ex.title, lang)}</p>
              <p>{pick(d.tagline, lang)}</p>
              <p className="overline !text-[#FDFBF7]/50">
                {pick({ en: "From", fr: "À partir de", es: "Desde" }, lang)}
              </p>
              <p className="font-medium text-[#D4A373]">{parsePrice(pick(d.practical.price, lang)) || pick(d.practical.price, lang)}</p>
            </div>
          </div>

          <div className="md:col-span-7">
            {status.success ? (
              <div
                className="bg-[#FDFBF7] text-[#2C2621] p-8 md:p-10 border border-[#D4A373]"
                data-testid="experience-request-success"
              >
                <p className="overline !text-[#C16542] flex items-center gap-2">
                  <Check className="w-3.5 h-3.5" strokeWidth={2.2} />
                  {pick({ en: "Thank you", fr: "Merci", es: "Gracias" }, lang)}
                </p>
                <p className="font-serif-x mt-4 text-2xl md:text-3xl leading-snug">
                  {pick(FORM_LABELS.thanks, lang)}
                </p>
                <button
                  type="button"
                  onClick={() => setStatus({ submitting: false, success: false, error: false })}
                  className="mt-8 inline-flex items-center gap-2 text-xs tracking-[0.25em] uppercase text-[#C16542] hover:text-[#A35133]"
                  data-testid="experience-request-reset"
                >
                  {pick({ en: "Send another request", fr: "Envoyer une autre demande", es: "Enviar otra solicitud" }, lang)}
                  <ArrowRight className="w-3.5 h-3.5" strokeWidth={1.5} />
                </button>
              </div>
            ) : (
              <form
                onSubmit={submitForm}
                className="bg-[#FDFBF7] text-[#2C2621] p-8 md:p-10 border border-[#FDFBF7]/15"
                data-testid="experience-request-form"
                noValidate
              >
                <div className="grid sm:grid-cols-2 gap-5">
                  <label className="block">
                    <span className="overline">{pick(FORM_LABELS.full_name, lang)} *</span>
                    <input
                      type="text"
                      name="full_name"
                      required
                      value={form.full_name}
                      onChange={(e) => setForm({ ...form, full_name: e.target.value })}
                      className="mt-2 w-full bg-transparent border-b border-[#2C2621]/25 focus:border-[#C16542] py-2.5 outline-none text-base"
                      data-testid="experience-form-name"
                    />
                  </label>
                  <label className="block">
                    <span className="overline">{pick(FORM_LABELS.email, lang)} *</span>
                    <input
                      type="email"
                      name="email"
                      required
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      className="mt-2 w-full bg-transparent border-b border-[#2C2621]/25 focus:border-[#C16542] py-2.5 outline-none text-base"
                      data-testid="experience-form-email"
                    />
                  </label>
                  <label className="block">
                    <span className="overline">{pick(FORM_LABELS.phone, lang)}</span>
                    <input
                      type="tel"
                      name="phone"
                      value={form.phone}
                      onChange={(e) => setForm({ ...form, phone: e.target.value })}
                      className="mt-2 w-full bg-transparent border-b border-[#2C2621]/25 focus:border-[#C16542] py-2.5 outline-none text-base"
                      data-testid="experience-form-phone"
                    />
                  </label>
                  <label className="block">
                    <span className="overline">{pick(FORM_LABELS.guests, lang)}</span>
                    <input
                      type="number"
                      name="guests"
                      min="1"
                      max="20"
                      value={form.guests}
                      onChange={(e) => setForm({ ...form, guests: e.target.value })}
                      className="mt-2 w-full bg-transparent border-b border-[#2C2621]/25 focus:border-[#C16542] py-2.5 outline-none text-base"
                      data-testid="experience-form-guests"
                    />
                  </label>
                  <label className="block sm:col-span-2">
                    <span className="overline">{pick(FORM_LABELS.preferred_date, lang)}</span>
                    <input
                      type="date"
                      name="preferred_date"
                      value={form.preferred_date}
                      onChange={(e) => setForm({ ...form, preferred_date: e.target.value })}
                      className="mt-2 w-full bg-transparent border-b border-[#2C2621]/25 focus:border-[#C16542] py-2.5 outline-none text-base"
                      data-testid="experience-form-date"
                    />
                  </label>
                  <label className="block sm:col-span-2">
                    <span className="overline">{pick(FORM_LABELS.notes, lang)}</span>
                    <textarea
                      name="notes"
                      rows={3}
                      value={form.notes}
                      onChange={(e) => setForm({ ...form, notes: e.target.value })}
                      className="mt-2 w-full bg-transparent border border-[#2C2621]/20 focus:border-[#C16542] p-3 outline-none text-base resize-none"
                      data-testid="experience-form-notes"
                    />
                  </label>
                </div>

                <div className="mt-6 flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-4">
                  <p className="text-xs text-[#5C5248]">
                    {pick({
                      en: "We typically reply within 24 hours.",
                      fr: "Nous répondons généralement sous 24 heures.",
                      es: "Solemos responder en 24 horas.",
                    }, lang)}
                  </p>
                  <button
                    type="submit"
                    disabled={status.submitting}
                    className="bg-[#C16542] hover:bg-[#A35133] disabled:opacity-60 text-[#FDFBF7] px-8 py-3.5 text-xs tracking-[0.25em] uppercase inline-flex items-center justify-center gap-2"
                    data-testid="experience-form-submit"
                  >
                    {status.submitting ? (
                      <>
                        <Loader2 className="w-3.5 h-3.5 animate-spin" strokeWidth={1.5} />
                        {pick(FORM_LABELS.sending, lang)}
                      </>
                    ) : (
                      <>
                        {pick(FORM_LABELS.submit, lang)}
                        <ArrowRight className="w-3.5 h-3.5" strokeWidth={1.5} />
                      </>
                    )}
                  </button>
                </div>

                {status.error && (
                  <p
                    className="mt-5 text-sm text-[#A35133] border-t border-[#A35133]/30 pt-4"
                    data-testid="experience-form-error"
                  >
                    {pick(FORM_LABELS.error, lang)}
                  </p>
                )}
              </form>
            )}
          </div>
        </div>
      </section>

      {/* RELATED HOTELS */}
      {relatedHotels.length > 0 && (
        <section className="py-20 md:py-28" data-testid="experience-related-hotels">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <div className="grid md:grid-cols-12 gap-8 mb-12">
              <div className="md:col-span-6">
                <p className="overline">
                  {pick({ en: "Stay near this experience", fr: "Séjourner près de cette expérience", es: "Hospédese cerca de esta experiencia" }, lang)}
                </p>
                <h2 className="font-serif-x mt-4 text-4xl md:text-5xl tracking-tight leading-[1.05]">
                  {pick({ en: "Related hotels", fr: "Hôtels associés", es: "Hoteles relacionados" }, lang)}
                </h2>
              </div>
              <div className="md:col-span-5 md:col-start-8 flex items-end">
                <p className="text-lg text-[#5C5248] leading-relaxed">
                  {pick({
                    en: "Each property is positioned as the natural base camp for this experience — book the room, we'll organise the rest.",
                    fr: "Chaque propriété est le camp de base naturel de cette expérience — réservez la chambre, nous organisons le reste.",
                    es: "Cada propiedad es el campamento base natural para esta experiencia — reserve la habitación, nosotros organizamos el resto.",
                  }, lang)}
                </p>
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
              {relatedHotels.map((h) => (
                <Link
                  key={h.slug}
                  to={`/hotels/${h.slug}`}
                  className="group relative aspect-[3/4] overflow-hidden"
                  data-testid={`experience-related-hotel-${h.slug}`}
                  data-cms-scope={`experience-related-hotel.${h.slug}`}
                >
                  <img src={h.image} alt={h.name} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" loading="lazy" decoding="async" data-cms-role="card.image"/>
                  <div className="absolute inset-0 bg-gradient-to-t from-[#1A1513]/85 to-transparent" />
                  <div className="absolute inset-0 p-5 flex flex-col justify-end text-[#FDFBF7]">
                    <div className="text-[10px] tracking-[0.2em] uppercase text-[#D4A373] flex items-center gap-1.5">
                      <MapPin className="w-3 h-3" strokeWidth={1.5} /> {h.location}
                    </div>
                    <div className="font-serif-x text-lg md:text-xl mt-1 leading-tight">{h.name}</div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* BACK TO EXPERIENCES */}
      <section className="bg-[#1A1513] text-[#FDFBF7] py-16" data-testid="experience-back-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div>
            <p className="overline !text-[#D4A373]">
              {pick({ en: "More to discover", fr: "À découvrir aussi", es: "Más para descubrir" }, lang)}
            </p>
            <h3 className="font-serif-x mt-3 text-3xl md:text-4xl leading-tight">
              {pick({
                en: "Explore all our experiences",
                fr: "Explorez toutes nos expériences",
                es: "Explore todas nuestras experiencias",
              }, lang)}
            </h3>
          </div>
          <Link
            to="/experiences"
            className="bg-[#C16542] hover:bg-[#A35133] text-[#FDFBF7] px-8 py-4 text-xs tracking-[0.2em] uppercase inline-flex items-center gap-2"
            data-testid="experience-back-to-list"
          >
            <ChevronLeft className="w-4 h-4" strokeWidth={1.5} />
            {pick({ en: "Back to experiences", fr: "Retour aux expériences", es: "Volver a experiencias" }, lang)}
          </Link>
        </div>
      </section>

      {lightboxOpen && (
        <Lightbox
          images={lightboxImages}
          title={pick(ex.title, lang)}
          onClose={() => setLightboxOpen(false)}
        />
      )}
    </div>
  );
};

export default ExperienceDetailPage;
